import { Button } from 'antd';
import { Menu, Dropdown } from 'antd';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';

import Icon from '../../../components/Icon/Icon'
import Popup from '../../../components/Popup/Popup';
import ScheduleEvent from '../../../components/ScheduleEvent/ScheduleEvent';
import Spinner from '../../../components/Spinner/Spinner';

import { CalendarItem, Meeting, Props, TabStatus } from './ScheduledCallsTab.model';

import styles from './ScheduledCallsTab.module.scss';

import { useScheduledCallsTab } from './useScheduledCallsTab';

const ScheduledCallsTab = ({ filters }: Props) => {

	const {
		deleteMeeting,
		formatDate,
		getMeeting,
		getMeetings,
		isAllowedToEdit,
		onClickParticipant,
		onUpdateEvent,
		state,
		toggleDeletePopup,
		toggleDetailsPopup,
		userId
	} = useScheduledCallsTab({ filters });
	const {
		meetings,
		meetingDetails,
		showDetailsPopup,
		showDeletePopup,
		tabStatus
	} = state;
	const [eventId, setEventId] = useState<string>('');
	const [meetingToBeDeleted, setMeetingToBeDeleted] = useState<string>('');

	const renderDropdown = (meeting: CalendarItem) => {
		return <Menu>
			<Menu.Item key="1">
				<Button type="link" onClick={() => {
					getMeeting(meeting.eventId);
					toggleDetailsPopup();
				}}>
					<FormattedMessage id="buttons.details" />
				</Button>
			</Menu.Item>
			{isAllowedToEdit(meeting) && <>

				<Menu.Item key="2">
					<Button type="link" onClick={() => {
						setEventId(meeting.eventId);
					}}>
						<FormattedMessage id="buttons.edit" />
					</Button>

				</Menu.Item>
				<Menu.Item key="3">
					<Button type="link" onClick={() => {
						toggleDeletePopup();
						setMeetingToBeDeleted(meeting.eventId);
					}}>
						<FormattedMessage id="buttons.delete" />
					</Button>
				</Menu.Item>
			</>}
		</Menu>
	}

	const renderDeletePopup = (eventId: string) => {
		return <Popup
			actions={<>
				<Button size="large" type="default" onClick={toggleDeletePopup}><FormattedMessage id="buttons.cancel" /></Button>
				<Button danger size="large" type="primary" onClick={() => {
					deleteMeeting(eventId);
					toggleDeletePopup();
				}}><FormattedMessage id="buttons.delete" /></Button>
			</>}
			onClose={toggleDeletePopup}
			title={{ action: 'delete', target: 'meeting' }}>

			<div className={styles.popupContent}>
				<FormattedMessage id="calendar.deleteEventQuestion" />
			</div>
		</Popup>
	}

	const renderDetailsPupup = (meetingDetails: Meeting) => {
		const { startTime, endTime } = meetingDetails;
		const { date, duration, endHour, startHour } = formatDate(startTime, endTime);
		return <Popup
			onClose={toggleDetailsPopup}
			title={{ action: '', target: 'details' }}
		>
			<div className={styles.popupContent}>
				<h2><FormattedMessage id="calendar.meetingDetails" /></h2>
				<div className={styles.column}>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.description" />
						</span>
						{meetingDetails && meetingDetails.description}
					</div>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.meetingTitle" />
						</span>
						{meetingDetails && meetingDetails.title}
					</div>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.date" />
						</span>
						{date}
					</div>
				</div>
				<div className={styles.column}>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.time" />
						</span>
						{startHour}-{endHour}
					</div>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.duration" />
						</span>
						{duration}
						{' '}
						<FormattedMessage id="calendar.minutes" />
					</div>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.link" />
						</span>
						{meetingDetails && <a href={meetingDetails.meetingUrl}>{meetingDetails.meetingUrl}</a>}
					</div>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.organizer" />
						</span>

						<div className={styles.participant} onClick={!meetingDetails.organizer.isDeleted ? onClickParticipant.bind(null, meetingDetails.organizer.userId): undefined}>
							<div className={styles.imgWrapper}>
								{meetingDetails.organizer.imageId && !meetingDetails.organizer.isDeleted ?
									<img src={meetingDetails.organizer.imageId} alt="" /> :
									<Icon name="user" width="16px" color="white"/>}
							</div>
							<span>{!meetingDetails.organizer.isDeleted ?
								<>{meetingDetails.organizer.firstName} {meetingDetails.organizer.lastName}</> :
								<FormattedMessage id="userLabels.deletedUser" />}
							</span>
						</div>
					</div>
					<div className={styles.field}>
						<span className={styles.label}>
							<FormattedMessage id="calendar.participants" />
						</span>
						{meetingDetails && meetingDetails.invitations.map((item, index) => {
							return <div className={styles.participant} onClick={!item.guest.isDeleted ? onClickParticipant.bind(null, item.guest.userId) : undefined}>
								<div className={styles.imgWrapper}>
									{item.guest.imageId && !item.guest.isDeleted ?
										<img src={item.guest.imageId} alt="" /> :
										<Icon name="user" width="16px" color="white"/>}
								</div>
								<span>{!item.guest.isDeleted ? <>{item.guest.firstName} {item.guest.lastName}</> : <FormattedMessage id="userLabels.deletedUser" />}</span>
							</div>
						})}
					</div>
				</div>
			</div>

		</Popup>
	}

	const renderMeetingCard = (meeting: CalendarItem) => {
		const { eventId, organizerImageId, guestImageIds } = meeting;
		return <div className={styles.card} key={eventId} >
			<div className={styles.imgWrapper} onClick={() => {
					getMeeting(meeting.eventId);
					toggleDetailsPopup();
				}}>
				{!organizerImageId && !guestImageIds.find(image => image !== null) ?
					<Icon name="calendar-alt" color="white" width="32px" height="32px" /> :
					<div className={styles.collage}>
						<div className={styles.collageImg}><img src={organizerImageId} alt="" /></div>
						<div className={styles.collageImg}>
							{guestImageIds.find(image => image !== null) ?
								<img src={guestImageIds.find(image => image !== null)} key={guestImageIds.find(image => image !== null)} alt="" /> :
								<Icon name="user" color="white" width="24px" height="24px" />
							}
						</div>
					</div>}
			</div>
			<h2>{meeting.title}</h2>
			<p>{formatDate(meeting.startTime, meeting.endTime).dateString}</p>
			<div className={styles.actions}>
				<Dropdown overlay={renderDropdown(meeting)}>
					<div className={styles.dropdownContainer}><Icon name="ellipsis" color="light" width="24px" height="32px" /></div>
				</Dropdown>
			</div>
		</div>
	}

	const renderMeetings = (meetings: CalendarItem[]) => {
		return meetings.map((meeting) =>
			renderMeetingCard(meeting)
		)
	}

	return <div className={styles.tabContent}>

		<Spinner delay={0} spinning={tabStatus === TabStatus.Loading} />
		<ErrorMessage onRefresh={getMeetings} showReport={true} error={tabStatus === TabStatus.Error} />
		
		<div className={styles.cards}>
			{meetings && meetings.length !== 0 ? renderMeetings(meetings) : <p className={styles.emptyContent}><FormattedMessage id="calendar.noMeetings" /></p>}
		</div>


		{showDetailsPopup && meetingDetails && renderDetailsPupup(meetingDetails)}
		{showDeletePopup && renderDeletePopup(meetingToBeDeleted)}
		{eventId &&
			<ScheduleEvent
				eventId={eventId}
				userId={userId}
				onClose={() => { setEventId(''); onUpdateEvent(); }} />
		}
	</div>
}
export default ScheduledCallsTab;